import { useMemo, useState } from 'react';
import { Search, ShieldCheck, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useScopePreview, useGrantAuditorScope } from '@/api/hooks/useCtsAudit';
import { groupLabel, type ScopePreviewRow } from '@/api/types/cts-audit.types';

// ── TC5 — the auditor scope BUILDER ──────────────────────────────────────────────────────────
//
// ⚠ THE FRAMEWORK IS HOW A SCOPE GETS BUILT, NOT WHAT IT MEANS.
//
// Picking "SOC 2" here says *resolve SOC 2 and grant me those controls*. What is stored is the
// resolved control set, frozen — so a later mapping change does not move what this auditor may
// see. That is a real difference the reviewer has to understand before confirming, which is why
// the confirmation says the number of CONTROLS and the word "frozen" rather than the framework's
// name alone.
//
// ⚠ AND THE COUNTS ON THIS SCREEN ARE A PREVIEW, NEVER THE GRANT. The server re-resolves against
// the entitled catalogue when the grant is made; nothing this component computes is trusted.

const PANEL = 'rounded-[10px] border border-cert-neutral-border bg-bg-surface';

export function ScopeBuilder({ tokenId, auditorName, onClose }: {
  tokenId: string; auditorName: string; onClose: () => void;
}) {
  const preview = useScopePreview();
  const grant = useGrantAuditorScope();
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [q, setQ] = useState('');
  const [confirming, setConfirming] = useState(false);

  const rows = preview.data ?? [];
  const visible = useMemo(() => {
    const n = q.trim().toLowerCase();
    return n === '' ? rows
      : rows.filter((r) => `${r.display_code} ${r.name}`.toLowerCase().includes(n));
  }, [rows, q]);

  // ⚠ DERIVED against the VISIBLE set, so "select all" cannot silently pick rows a filter is
  // hiding — the selection the reviewer confirms is the selection they can see.
  const allVisiblePicked = visible.length > 0 && visible.every((r) => picked.has(r.framework_reference));

  const toggle = (ref: string) => setPicked((prev) => {
    const next = new Set(prev);
    if (next.has(ref)) next.delete(ref); else next.add(ref);
    return next;
  });

  const chosen = rows.filter((r) => picked.has(r.framework_reference));
  /**
   * ⚠ AN UPPER BOUND, AND LABELLED AS ONE. Two frameworks can share controls, so summing their
   * counts over-states the union — the exact number is what the server resolves. Presenting this
   * sum as "the scope" would promise access the grant does not carry.
   */
  const upperBound = chosen.reduce((n, r) => n + r.control_count, 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-[20px]">
      <div role="dialog" aria-modal="true" aria-labelledby="scope-title"
        className={cn(PANEL, 'flex max-h-[86vh] w-full max-w-[720px] flex-col p-[22px]')}>
        <h2 id="scope-title" className="text-[17px] font-semibold text-text-primary">
          Build the audit scope
        </h2>
        <p className="mt-[5px] text-[13px] leading-[1.55] text-text-secondary">
          Choose the frameworks {auditorName} is auditing. VeriGRC resolves them to the controls
          that satisfy their requirements, and grants those controls.
        </p>

        {!confirming ? (
          <>
            <div className="relative mt-[16px]">
              <Search className="pointer-events-none absolute left-[12px] top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-text-faint" aria-hidden="true" />
              <input type="search" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Search frameworks" aria-label="Search frameworks"
                className="w-full rounded-[9px] border border-cert-neutral-border bg-bg-surface-2 py-[9px] pl-[36px] pr-[12px] text-[13.5px] text-text-primary placeholder:text-text-faint focus:border-accent-cyan focus:outline-none" />
            </div>

            <div className="mt-[10px] flex items-center justify-between">
              <label className="flex items-center gap-[8px] text-[12.5px] text-text-secondary">
                <input type="checkbox" checked={allVisiblePicked} aria-label="Select all frameworks"
                  onChange={() => setPicked((prev) => {
                    const next = new Set(prev);
                    visible.forEach((r) => allVisiblePicked
                      ? next.delete(r.framework_reference) : next.add(r.framework_reference));
                    return next;
                  })} />
                Select all shown ({visible.length})
              </label>
              {picked.size > 0 && (
                <button type="button" onClick={() => setPicked(new Set())}
                  className="text-[12.5px] font-medium text-accent-cyan hover:opacity-80">
                  Clear selection
                </button>
              )}
            </div>

            <div className="mt-[12px] min-h-0 flex-1 overflow-y-auto rounded-[9px] border border-border-subtle">
              {preview.isLoading ? (
                <p className="px-[14px] py-[18px] text-[13px] text-text-secondary">Loading frameworks…</p>
              ) : visible.length === 0 ? (
                <p className="flex items-center gap-[9px] px-[14px] py-[18px] text-[13px] text-text-secondary">
                  <ShieldCheck className="h-[15px] w-[15px] shrink-0 text-cert-valid-fg" aria-hidden="true" />
                  {rows.length === 0
                    ? 'No frameworks are licensed for this tenant yet.'
                    : 'No framework matches that search.'}
                </p>
              ) : visible.map((r: ScopePreviewRow) => (
                <label key={r.framework_reference}
                  className="flex cursor-pointer items-start gap-[11px] border-b border-border-subtle px-[14px] py-[11px] last:border-b-0 hover:bg-bg-surface-2">
                  <input type="checkbox" className="mt-[3px]"
                    checked={picked.has(r.framework_reference)}
                    onChange={() => toggle(r.framework_reference)}
                    aria-label={`Include ${r.name}`} />
                  <span className="min-w-0">
                    <span className="block text-[13.5px] font-medium text-text-primary">{r.name}</span>
                    <span className="mt-[2px] block font-mono text-[11.5px] text-text-faint">
                      {/* ⚠ The framework's OWN level name, from its data — never the word "family",
                          which is VeriGRC's control taxonomy and a different hierarchy entirely. */}
                      {r.display_code} · {groupLabel(r)} · {r.requirement_count} requirements
                      {' · '}
                      <span className="text-text-secondary">{r.control_count} unique controls</span>
                    </span>
                  </span>
                </label>
              ))}
            </div>

            <div className="mt-[16px] flex items-center justify-end gap-[10px]">
              <button type="button" onClick={onClose}
                className="rounded-[7px] px-[12px] py-[8px] text-[13px] text-text-secondary hover:text-text-primary">
                Cancel
              </button>
              <button type="button" disabled={picked.size === 0} onClick={() => setConfirming(true)}
                className="rounded-[7px] bg-accent-cyan px-[14px] py-[8px] text-[13px] font-medium text-bg-base disabled:opacity-40">
                Review scope
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="mt-[16px] min-h-0 flex-1 overflow-y-auto">
              <ul className={cn(PANEL, 'overflow-hidden')}>
                {chosen.map((r) => (
                  <li key={r.framework_reference}
                    className="flex items-baseline justify-between gap-[12px] border-b border-border-subtle px-[14px] py-[10px] last:border-b-0">
                    <span className="text-[13.5px] text-text-primary">{r.name}</span>
                    <span className="font-mono text-[11.5px] text-text-faint">
                      {groupLabel(r)} · {r.requirement_count} requirements · {r.control_count} controls
                    </span>
                  </li>
                ))}
              </ul>

              <p className="mt-[14px] flex items-start gap-[9px] rounded-[9px] border border-cert-pending-border bg-cert-pending-bg/40 px-[13px] py-[11px] text-[12.5px] leading-[1.55] text-text-secondary">
                <AlertTriangle className="mt-[1px] h-[15px] w-[15px] shrink-0 text-cert-pending-fg" aria-hidden="true" />
                <span>
                  {/* ⚠ THE FREEZE IS THE THING TO UNDERSTAND BEFORE CONFIRMING. Said plainly,
                      because the alternative — discovering it when the library changes — is the
                      moment nobody wants to have. */}
                  <strong className="text-text-primary">The resolved controls are frozen into this grant.</strong>{' '}
                  If the library later maps these frameworks differently, this auditor&apos;s access does
                  not change. Widening or narrowing it is a new grant.
                  <br /><br />
                  Up to <strong className="text-text-primary">{upperBound} controls</strong> —
                  frameworks can share controls, so the granted set may be smaller. The exact number
                  is confirmed when the scope is saved.
                </span>
              </p>
            </div>

            <div className="mt-[16px] flex items-center justify-end gap-[10px]">
              <button type="button" onClick={() => setConfirming(false)}
                className="rounded-[7px] px-[12px] py-[8px] text-[13px] text-text-secondary hover:text-text-primary">
                Back
              </button>
              <button type="button" disabled={grant.isPending}
                onClick={() => grant.mutate(
                  { tokenId, frameworkReferences: [...picked] },
                  { onSuccess: onClose })}
                className="rounded-[7px] bg-accent-cyan px-[14px] py-[8px] text-[13px] font-medium text-bg-base disabled:opacity-40">
                {grant.isPending ? 'Saving…' : 'Grant this scope'}
              </button>
            </div>
            {grant.isError && (
              <p className="mt-[10px] text-[12.5px] text-cert-expired-fg">
                That selection resolved to no controls — nothing was granted.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
